/**
 * CommitFlow — Wave 7 commit-pipeline UI orchestrator.
 *
 * Replaces the placeholder Submit step with a 3-screen flow:
 *
 *   1. Review            — full cart summary (re-uses ReviewStep)
 *   2. Confirm           — modal asking "open N accounts for {customer}?"
 *   3. Commit-in-progress — runs POST /signing/start first when the cart has
 *                           no SigningGroupId (S1.2 prerequisite — creates the
 *                           SigningSession the pipeline's DisclosurePrefill
 *                           step gates on), then /v3/carts/{cartId}/commit,
 *                           then polls /commit/status every 2 s and renders
 *                           CommitProgressBar.
 *
 * S1.1 (2026-06-11): the PRIMARY submit click for draft carts is ReviewStep's
 * "Submit Application →" + PreSubmitConfirmModal (Round-3-locked SPEC Screen
 * 27) — CartDetail mounts THIS component once the cart is in an in-progress /
 * terminal commit state (awaiting-signatures | committing | committed |
 * pipeline-error), where it auto-jumps to screen 3 and live-tracks the
 * pipeline through the signing pause to completion. Screens 1-2 here remain
 * the re-confirm path for hosts that mount the flow on a pre-commit cart.
 *
 * Sub-states surfaced inline on screen 3 (no extra modals — keeps the FSR
 * locked-in until terminal):
 *   - awaiting-signatures → AwaitingSignaturesBanner above the progress bar
 *   - pipeline-error      → CommitErrorPanel (retry / abort)
 *   - committed           → success summary + "Back to RAO" CTA
 *
 * Hard rules per Bill 2026-04-24:
 *   - Never surface cart IDs / GUIDs / raw codes — humanize everything.
 *   - PCB design tokens; no hex literals.
 *   - The 3-screen lock-in mirrors the SP-Hub close-out flow Bill described
 *     during the Wave 6.2 walk-through (no nav-away on screen 2 or 3).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  CartDto,
  CommitOutcome,
  CommitStatus,
} from '@retail-ops/shared-types';
import { ApiError } from '@retail-ops/api-client';
import { Banner, Button, ModalShell, Pill } from '@/components/primitives';
import { TestModeBadge } from '@/modules/_shared/TestModeBadge';
import { useApi } from './hooks/useApi';
import { useConfig } from '@/contexts/ConfigContext';
import { useAuth } from '@/contexts/AuthContext';
import {
  summarizePrimaryApplicant,
  summarizeProductChips,
  humanizeBranch,
} from './utils/cartCardData';
import { ensureSigningSessionForCart } from './services/signingSetup';
import { ReviewStep } from './steps/ReviewStep';
import { CommitProgressBar } from './CommitProgressBar';
import { PipelineTimeline } from './PipelineTimeline';
import { CommitCompletionPanel } from './CommitCompletionPanel';
import { CommitErrorPanel } from './CommitErrorPanel';
import { AwaitingSignaturesBanner } from './AwaitingSignaturesBanner';

export interface CommitFlowProps {
  cart: CartDto;
  /** Raised whenever the parent should re-fetch / sync the cart row. */
  onCartUpdated?: (cart: CartDto) => void;
  /** Raised when the FSR closes the flow (terminal success or back-to-RAO). */
  onExit?: () => void;
  /** Bill 2026-10-02 — re-open the in-branch ceremony for signers still outstanding after commit. */
  onResumeSigning?: () => void;
}

type FlowScreen = 'review' | 'in-progress';

/** Which leg of the submit chain is running — drives the confirm-button label. */
type SubmitPhase = 'idle' | 'signing-setup' | 'committing';

const POLL_INTERVAL_MS = 2000;
/** CHG-0138: completion-record refresh while signatures are outstanding. */
const SIGNATURE_REFRESH_MS = 5_000;
const SIGNATURE_REFRESH_MAX_MS = 15 * 60_000;

function isInProgress(status: CartDto['status']): boolean {
  return (
    status === 'committing'
    || status === 'awaiting-signatures'
    || status === 'pipeline-error'
  );
}

function isTerminal(status: CartDto['status']): boolean {
  return status === 'committed' || status === 'archived';
}

export function CommitFlow({
  cart,
  onCartUpdated,
  onExit,
  onResumeSigning,
}: CommitFlowProps): JSX.Element {
  const { client, makeSignal } = useApi();
  const { config } = useConfig();
  const { user } = useAuth();

  // The screen state. We auto-jump to 'in-progress' when the cart is already
  // mid-pipeline (e.g. FSR navigated away + came back during awaiting-signatures).
  const [screen, setScreen] = useState<FlowScreen>(
    isInProgress(cart.status) || isTerminal(cart.status) ? 'in-progress' : 'review',
  );
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitPhase, setSubmitPhase] = useState<SubmitPhase>('idle');
  const [submitError, setSubmitError] = useState<string | null>(null);
  // BACKLOG-1750: pipeline progress lives in a window, not on the page.
  const [historyOpen, setHistoryOpen] = useState(false);
  const [autoProgress, setAutoProgress] = useState(true);
  const [showCalls, setShowCalls] = useState(false);

  // Live status payload — populated on first poll after commit kicks off.
  const [liveStatus, setLiveStatus] = useState<CommitStatus | null>(null);

  // Polling lifecycle: only run while the cart is in a non-terminal in-progress
  // state. We bind to cart.cartId + cart.status — if the parent updates the
  // cart, the effect re-evaluates.
  const pollAbortRef = useRef<AbortController | null>(null);

  // Humanized header summary (shared between review + confirm + progress).
  const applicant = summarizePrimaryApplicant(cart);
  const productChips = summarizeProductChips(cart, config.products);
  const branchName = humanizeBranch(cart.branch, config.branches);
  const accountCount = productChips.length;

  // Summarize how the customer-facing confirm message reads. Uses the chip
  // names so it reads "open Free Checking and SAV" vs "open 2 accounts".
  const productSummary = useMemo<string>(() => {
    if (productChips.length === 0) return '0 accounts';
    if (productChips.length === 1) {
      const first = productChips[0];
      return first ? first.name : '1 account';
    }
    if (productChips.length === 2) {
      const a = productChips[0]?.name ?? 'one product';
      const b = productChips[1]?.name ?? 'another product';
      return `${a} + ${b}`;
    }
    return `${productChips.length} accounts`;
  }, [productChips]);

  // -----------------------------------------------------------------------
  // Polling
  // -----------------------------------------------------------------------

  const fetchStatusOnce = useCallback(
    async (signal: AbortSignal): Promise<CommitStatus | null> => {
      try {
        const res = await client.retail.carts.getCommitStatus(cart.cartId, { signal });
        return res;
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return null;
        // Soft-fail polls — the next tick may succeed.
        return null;
      }
    },
    [client, cart.cartId],
  );

  // CHG-0138 (Mackenzie 10/29): under never-wait the commit finishes before the customer signs, so the
  // completion record the main poll stopped on still says "0 documents signed" and lists the signers as
  // outstanding. The server refreshes the record as each signer finishes (CHG-0055); keep re-reading it
  // while signatures are outstanding, every 5 s for up to 15 minutes, and once when a finished
  // application is opened.
  const summaryForSignatures = liveStatus?.completionSummary;
  const signaturesOutstanding =
    !!summaryForSignatures &&
    ((summaryForSignatures.signersOutstanding?.length ?? 0) > 0 ||
      (summaryForSignatures.signaturesCaptured ?? 0) < (summaryForSignatures.signaturesRequired ?? 0));
  const finished = cart.status === 'committed';
  useEffect(() => {
    if (!finished) return;
    if (liveStatus && !signaturesOutstanding) return;
    const ctrl = new AbortController();
    let cancelled = false;
    const startedAt = Date.now();
    async function tick(): Promise<void> {
      if (cancelled) return;
      const snap = await fetchStatusOnce(ctrl.signal);
      if (cancelled) return;
      if (snap) setLiveStatus(snap);
      if (!cancelled && Date.now() - startedAt < SIGNATURE_REFRESH_MAX_MS) {
        window.setTimeout(() => { void tick(); }, SIGNATURE_REFRESH_MS);
      }
    }
    void tick();
    return () => {
      cancelled = true;
      ctrl.abort();
    };
    // Re-arms only when the outstanding state flips; each tick replaces liveStatus.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finished, signaturesOutstanding, cart.cartId, fetchStatusOnce]);

  useEffect(() => {
    // Only poll while we're in-progress AND on screen 3.
    if (screen !== 'in-progress') return;
    if (!isInProgress(cart.status)) return;

    const ctrl = new AbortController();
    pollAbortRef.current = ctrl;
    let cancelled = false;

    async function tick(): Promise<void> {
      if (cancelled) return;
      const snap = await fetchStatusOnce(ctrl.signal);
      if (cancelled) return;
      if (snap) {
        setLiveStatus(snap);
        // If the cart-level status changed, ask parent to sync. We never
        // mutate `cart` directly here. Guard on a TRUTHY cartStatus: a missing/
        // empty status (mapping miss, 404 mid-poll) must never count as a
        // "transition" — that was the 2s-reload runaway (snap.cartStatus came
        // back undefined because the status envelope wasn't unwrapped; fixed in
        // the api-client, and this guard makes the loop structurally impossible).
        if (snap.cartStatus && snap.cartStatus !== cart.status && onCartUpdated) {
          // The parent owns the canonical re-fetch; we just signal a sync.
          // We pass the locally-known cart with the latest status patched in
          // so parents that don't re-fetch still render the right shell.
          onCartUpdated({ ...cart, status: snap.cartStatus });
        }
        if (isTerminal(snap.cartStatus)) {
          // Stop polling on terminal.
          return;
        }
      }
      if (!cancelled) {
        window.setTimeout(() => { void tick(); }, POLL_INTERVAL_MS);
      }
    }
    // First tick fires immediately (after the commit kick-off resolves).
    void tick();

    return () => {
      cancelled = true;
      ctrl.abort();
    };
    // We intentionally bind to cart.cartId + cart.status — passing `cart`
    // directly would re-run on every cart update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, cart.cartId, cart.status]);

  // ── One-shot status fetch for terminal carts (Bill smoke-test round 2,
  // 2026-07-10) ── an already-committed cart never arms the poll above, so
  // liveStatus (and its completionSummary) stayed null and the completion
  // panel would have nothing to show. Fetch the snapshot once on mount.
  const terminalFetchedRef = useRef(false);
  useEffect(() => {
    if (screen !== 'in-progress') return;
    if (!isTerminal(cart.status)) return;
    if (terminalFetchedRef.current || liveStatus?.completionSummary) return;
    terminalFetchedRef.current = true;
    const ctrl = new AbortController();
    void (async () => {
      const snap = await fetchStatusOnce(ctrl.signal);
      if (snap) setLiveStatus(snap);
    })();
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, cart.cartId, cart.status]);

  // -----------------------------------------------------------------------
  // Commit kick-off
  //
  // Leg 1 (S1.2, 2026-06-11): /signing/start prerequisite via the shared
  // ensureSigningSessionForCart helper (same code path the primary
  // PreSubmitConfirmModal ceremony uses). Idempotent — skipped when
  // cart.signingGroupId is already bound.
  // -----------------------------------------------------------------------

  const submitCommit = useCallback(async (): Promise<void> => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      // Leg 1 — signing setup (SigningSession + cart.SigningGroupId).
      setSubmitPhase('signing-setup');
      await ensureSigningSessionForCart({
        cart,
        client,
        config: {
          disclosures: config.disclosures,
          entityRules: config.entityRules,
          entityRequiredDocs: config.entityRequiredDocs,
          products: config.products,
        },
        ...(user?.upn ? { fsrEmail: user.upn } : {}),
        ...(user?.displayName ? { fsrName: user.displayName } : {}),
        signal: makeSignal(),
      });

      // Leg 2 — the commit pipeline itself. NO abort signal: an unmount-scoped
      // signal cancelled the pipeline mid-Core-write when the status poll
      // swapped panels (2026-07-02 incident). State-changing banking POSTs
      // never carry unmount-abort signals.
      setSubmitPhase('committing');
      const outcome: CommitOutcome = await client.retail.carts.commit(cart.cartId);
      if (!outcome.success) {
        setSubmitError(outcome.error ?? 'Commit could not start. Resolve the blockers and try again.');
        return;
      }
      setConfirmOpen(false);
      setScreen('in-progress');
      if (outcome.status) setLiveStatus(outcome.status);
      // Always nudge the parent to re-fetch — the commit response envelope
      // doesn't carry the full cart row, and the status poll only arms once
      // the parent's cart.status reflects an in-progress state. Patch the
      // local status optimistically; the parent's reload fetches the truth
      // (typically 'awaiting-signatures' after the step-6 pause).
      if (onCartUpdated) {
        onCartUpdated(outcome.cart ?? { ...cart, status: 'committing' });
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      const message = ApiError.isApiError(err)
        ? err.message
        : err instanceof Error
          ? err.message
          : 'Unexpected error.';
      setSubmitError(message);
    } finally {
      setSubmitting(false);
      setSubmitPhase('idle');
    }
  }, [client, cart, makeSignal, onCartUpdated, config.disclosures,
    config.entityRules, config.entityRequiredDocs, config.products,
    user?.upn, user?.displayName]);

  // -----------------------------------------------------------------------
  // Renders
  // -----------------------------------------------------------------------

  if (screen === 'review') {
    return (
      <section
        aria-labelledby="commit-flow-review-h"
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-7)' }}
      >
        <header style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
          <h2
            id="commit-flow-review-h"
            style={{ margin: 0, fontSize: 18, color: 'var(--pcb-blue-dark)' }}
          >
            Final review
          </h2>
          <p style={{ fontSize: 13, color: 'var(--ink-700)', margin: 0 }}>
            Confirm everything below. Submitting starts the live commit pipeline — accounts
            post, disclosures dispatch, and signatures pause the flow until your customer
            signs.
          </p>
        </header>

        <ReviewStep cart={cart} />

        <div
          style={{
            display: 'flex',
            gap: 'var(--sp-4)',
            justifyContent: 'flex-end',
            paddingTop: 'var(--sp-5)',
            borderTop: `1px solid var(--ink-200)`,
          }}
        >
          {onExit && (
            <Button variant="ghost" onClick={onExit}>
              Back
            </Button>
          )}
          <Button variant="primary" onClick={() => setConfirmOpen(true)}>
            Submit for opening
          </Button>
        </div>

        {confirmOpen && (
          <ConfirmDialog
            open={confirmOpen}
            applicantLabel={applicant.label}
            isTestMode={cart.isTestMode === true}
            accountCount={accountCount}
            productSummary={productSummary}
            branchName={branchName}
            submitting={submitting}
            submitPhase={submitPhase}
            submitError={submitError}
            onCancel={() => {
              setConfirmOpen(false);
              setSubmitError(null);
            }}
            onConfirm={() => { void submitCommit(); }}
          />
        )}
      </section>
    );
  }

  // ----- screen === 'in-progress' -----

  const cartStatus = liveStatus?.cartStatus ?? cart.status;
  const isCommitted = cartStatus === 'committed';
  const isErrored = cartStatus === 'pipeline-error';
  const isAwaiting = cartStatus === 'awaiting-signatures';

  const stateBanner = isCommitted ? (
    <Banner tone="ok" title="All accounts opened">
      The pipeline finished — here&rsquo;s exactly what was created, and what to do next.
    </Banner>
  ) : isErrored ? null /* CommitErrorPanel handles its own banner */ : isAwaiting ? null : (
    <Banner tone="info" title="Commit in progress">
      {/* Bill 2026-07-13 Item 30: an animated spinner so the FSR sees the
          backend IS working after signatures are collected (was: static
          text that read as a lock-up). */}
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
        <span
          aria-hidden
          style={{
            width: 14, height: 14, flexShrink: 0,
            border: '2px solid var(--pcb-blue)',
            borderTopColor: 'transparent', borderRadius: '50%',
            display: 'inline-block', animation: 'pcb-spin 0.7s linear infinite',
          }}
        />
        <span>
          We&rsquo;re posting the new account(s) and generating the disclosure package.
          Stay on this screen — it updates live every couple of seconds.
        </span>
      </span>
    </Banner>
  );

  return (
    <section
      aria-labelledby="commit-flow-progress-h"
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-7)' }}
    >
      <header style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 'var(--sp-4)',
          }}
        >
          <h2
            id="commit-flow-progress-h"
            style={{ margin: 0, fontSize: 18, color: 'var(--pcb-blue-dark)' }}
          >
            {isCommitted ? 'Accounts opened' : 'Opening the accounts'}
          </h2>
          <div style={{ display: 'inline-flex', gap: 'var(--sp-3)', alignItems: 'center' }}>
            {cart.isTestMode === true && <TestModeBadge active size="sm" />}
            <Pill
              variant={
                isCommitted ? 'ok'
                : isErrored ? 'err'
                : isAwaiting ? 'warn'
                : 'info'
              }
            >
              {isCommitted ? 'Completed'
                : isErrored ? 'Needs attention'
                : isAwaiting ? 'Awaiting signatures'
                : 'In progress'}
            </Pill>
          </div>
        </div>
        <div style={{ fontSize: 12, color: 'var(--ink-600)' }}>
          {productSummary} at {branchName}
        </div>
      </header>

      {stateBanner}

      {/* REQ-167 — wedge honesty banner. The server detected committed-but-
          unfinished Core Director work and has ALREADY queued recovery; the
          FSR needs reassurance + a truthful reason, not a task. */}
      {liveStatus?.wedged === true && (
        <Banner tone="warn" title="Pipeline stalled — recovering automatically">
          {liveStatus.wedgedReason ??
            'Some post-signing work never finished. Recovery has been re-queued automatically.'}{' '}
          No action needed — this screen updates live. If it hasn&rsquo;t cleared in a few
          minutes, the cart will surface a Retry option.
        </Banner>
      )}

      {/* CHG-0173 (Hub ask #286) warn mode — Core Director recorded a change under the
          application's officer because the person acting could not be confirmed. Nothing
          was stopped; the officer is told in plain words. */}
      {(liveStatus?.operatorWarnings?.length ?? 0) > 0 && (
        <Banner tone="warn" title="Core Director operator">
          {liveStatus!.operatorWarnings!.map((w) => (
            <div key={w}>{w}</div>
          ))}
        </Banner>
      )}

      {isAwaiting && (
        <AwaitingSignaturesBanner
          cart={cart}
          {...(typeof liveStatus?.signaturesCollected === 'number'
            ? { signaturesCollected: liveStatus.signaturesCollected }
            : {})}
          {...(typeof liveStatus?.signaturesTotal === 'number'
            ? { signaturesTotal: liveStatus.signaturesTotal }
            : {})}
          {...(typeof liveStatus?.signingExpiresInDays === 'number'
            ? { signingExpiresInDays: liveStatus.signingExpiresInDays }
            : {})}
        />
      )}

      {isErrored && (
        <CommitErrorPanel
          cart={cart}
          {...(liveStatus ? { status: liveStatus } : {})}
          onRetried={(outcome) => {
            if (outcome.cart && onCartUpdated) onCartUpdated(outcome.cart);
            if (outcome.status) setLiveStatus(outcome.status);
          }}
          onAborted={(resolved) => {
            if (onCartUpdated) onCartUpdated(resolved);
            if (onExit) onExit();
          }}
        />
      )}

      {/* Completion confirmation (Bill smoke-test round 2, 2026-07-10) —
          accounts opened / customer records / docs signed / committed-at,
          plus Download-for-Aurora + Back actions. Replaces the bare
          "Back to applications" button that left the FSR guessing. */}
      {isCommitted && (
        <CommitCompletionPanel
          cart={cart}
          {...(liveStatus?.completionSummary ? { summary: liveStatus.completionSummary } : {})}
          {...(onExit ? { onExit } : {})}
          {...(onResumeSigning ? { onResumeSigning } : {})}
        />
      )}

      {/* BACKLOG-1750 (Bill + Connor): the pipeline is not a permanent list on the page. While the
          commit runs it shows in a progress window (opens on its own, can be hidden); afterwards
          "View processing history" opens the same window. "Details" there is the REQ-167 per-call
          timeline: every Core Director call in execution order with its result. */}
      {(liveStatus?.steps?.length ?? 0) > 0 && (
        <div>
          <Button variant="ghost" size="sm" onClick={() => setHistoryOpen(true)}>
            {isCommitted || isErrored || isAwaiting ? 'View processing history' : 'Show progress'}
          </Button>
        </div>
      )}
      <ModalShell
        open={historyOpen || (autoProgress && !isCommitted && !isErrored && !isAwaiting && (liveStatus?.steps?.length ?? 0) > 0)}
        onClose={() => { setHistoryOpen(false); setAutoProgress(false); }}
        title={isCommitted ? 'Processing history' : isErrored ? 'Processing history — needs attention' : 'Opening the accounts'}
        size="lg"
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
          {!isCommitted && !isErrored && !isAwaiting && (
            <span style={{ fontSize: 13, color: 'var(--ink-700)' }}>
              Each step ticks off as Core Director confirms it. You can hide this window; the work continues.
            </span>
          )}
          <CommitProgressBar
            steps={liveStatus?.steps ?? []}
            {...(liveStatus?.currentStepKey ? { currentStepKey: liveStatus.currentStepKey } : {})}
          />
          {liveStatus?.granularSteps && liveStatus.granularSteps.length > 0 && (
            <div>
              <Button variant="link" size="sm" onClick={() => setShowCalls((v) => !v)}>
                {showCalls ? 'Hide details' : `Details (${liveStatus.granularSteps.length} Core Director calls)`}
              </Button>
              {showCalls && <PipelineTimeline steps={liveStatus.granularSteps} />}
            </div>
          )}
        </div>
      </ModalShell>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Confirm dialog (screen 2)
// ---------------------------------------------------------------------------

interface ConfirmDialogProps {
  open: boolean;
  applicantLabel: string;
  isTestMode: boolean;
  accountCount: number;
  productSummary: string;
  branchName: string;
  submitting: boolean;
  submitPhase: SubmitPhase;
  submitError: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}

function ConfirmDialog({
  open,
  applicantLabel,
  isTestMode,
  accountCount,
  productSummary,
  branchName,
  submitting,
  submitPhase,
  submitError,
  onCancel,
  onConfirm,
}: ConfirmDialogProps): JSX.Element {
  const accountWord = accountCount === 1 ? 'account' : 'accounts';
  const busyLabel = submitPhase === 'signing-setup'
    ? 'Preparing disclosures…'
    : 'Submitting…';
  return (
    <ModalShell
      open={open}
      onClose={() => { if (!submitting) onCancel(); }}
      title="Open accounts now?"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onCancel} disabled={submitting}>
            Not yet
          </Button>
          <Button
            variant="primary"
            onClick={onConfirm}
            disabled={submitting || accountCount === 0}
          >
            {submitting ? busyLabel : `Open ${accountCount} ${accountWord}`}
          </Button>
        </>
      }
    >
      <div style={{ fontSize: 13, color: 'var(--ink-700)', lineHeight: 1.5 }}>
        You&rsquo;re about to open <strong>{accountCount} {accountWord}</strong> for{' '}
        <strong>{applicantLabel}</strong>{isTestMode && (
          <>
            {' '}
            <TestModeBadge active size="sm" />
          </>
        )}.
      </div>
      <ul
        style={{
          margin: 0,
          paddingLeft: 'var(--sp-7)',
          fontSize: 12,
          color: 'var(--ink-700)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-2)',
        }}
      >
        <li><strong>Products:</strong> {productSummary}</li>
        <li><strong>Branch:</strong> {branchName}</li>
        <li>
          <strong>What happens next:</strong> we post the account(s), pre-fill disclosures, and
          send signing invites. The pipeline pauses until your customer signs.
        </li>
      </ul>
      {isTestMode && (
        <Banner tone="info" title="Test Mode is on">
          No real account post, no live disclosures, no notifications. Safe to rehearse.
        </Banner>
      )}
      {submitError && (
        <Banner tone="err">{submitError}</Banner>
      )}
    </ModalShell>
  );
}
